import { useEffect, useState, type ChangeEvent, type FocusEvent } from 'react';
import type { FieldErrors, UseFormSetValue } from 'react-hook-form';
import Text from "@/components/forms/text";
import PostalCodeCity from "@/components/forms/postal-code-city";
import { subYears } from 'date-fns';
import { getCountries } from "@/lib/helpers";
import RadioGroup from "@/components/forms/radio-group";
import Autocomplete, { type AutocompleteItem } from "@/components/forms/autocomplete";
import Select from "@/components/forms/select";
import { tiersPayeurRelation } from "@/lib/options/select-options";
import SimpleDatePicker from "@/components/forms/simple-date-picker";
import AutoCompleteBirth from "@/components/forms/auto-complete-birth";
import { Button } from '@/components/ui/button';
import { X, Pencil } from 'lucide-react';
import { useCurrentUser } from '@/hooks/api/use-user';
import { DATE_ISO, formatDate } from '@/lib/dates';
import type { ChangeRibValues } from '@/schemas/change-rib';
import type { Contract } from '@/types/api/contract';

interface Country {
    id: string;
    name: string;
}

interface ChangeTitulaireProps {
    values: ChangeRibValues;
    errors: FieldErrors<ChangeRibValues>;
    handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleBlur: (e: FocusEvent<HTMLInputElement>) => void;
    changeTitulaire: boolean;
    setChangeTitulaire: (value: boolean) => void;
    setValue: UseFormSetValue<ChangeRibValues>;
    contrat: Partial<Contract>;
    souscripteur?: string | null;
}

const ChangeTitulaire = (props: ChangeTitulaireProps) => {
    const { values, errors, handleChange, handleBlur, changeTitulaire, setChangeTitulaire, setValue, contrat, souscripteur } = props;
    const titulaire = useCurrentUser().banque?.titulaire;
    const [countries, setCountries] = useState<AutocompleteItem[]>();

    const handleChangeTitulaire = () => {
        setChangeTitulaire(false);
        setValue('type', '', { shouldValidate: true });
        setValue('adresse', '', { shouldValidate: true });
        setValue('cp', '', { shouldValidate: true });
        setValue('ville', '', { shouldValidate: true });
        setValue('relationship', '', { shouldValidate: true });
        setValue('titulaire', '', { shouldValidate: true });
        setValue('firstname', '', { shouldValidate: true });
        setValue('lastname', '', { shouldValidate: true });
        setValue('birthname', '', { shouldValidate: true });
        setValue('birthdate', '', { shouldValidate: true });
        setValue('birthcity', '', { shouldValidate: true });
        setValue('birthcountry', '', { shouldValidate: true });
        setValue('relationship', '', { shouldValidate: true });
        setValue('third_payeur_reason', '', { shouldValidate: true });
        setValue('files', {}, { shouldValidate: true });
    };

    useEffect(() => {
        void (async function fetchCountries() {
            const countries = await getCountries<Country[]>();
            const listcountries = countries.map(c => ({
                value: c.id,
                label: c.name
            }));
            setCountries(listcountries);
        })();
    }, []);

    const handleChangeTitulaireField = (e: string) => {
        if (contrat.id) {
            if (!e || (souscripteur ?? '').trim().toUpperCase() === e.trim().toUpperCase()) {
                handleChangeType(null);
                clearFieldsThirdPartyPayer();
            } else {
                setValue('contract', contrat.id, { shouldValidate: true });
            }
        }
    };

    const clearFieldsThirdPartyPayer = (type: string | null = 'all') => {
        if (type === "M" || type === 'all') {
            setValue('firstname', '', { shouldValidate: true });
            setValue('birthname', '', { shouldValidate: true });
            setValue('birthdate', '', { shouldValidate: true });
            setValue('birthcountry', '', { shouldValidate: true });
            setValue('birthcity', '', { shouldValidate: true });
            setValue('relationship', '', { shouldValidate: true });
            setValue('third_payeur_reason', '', { shouldValidate: true });
        }
        if (type === 'all') {
            setValue('type', '', { shouldValidate: true });
            setValue('lastname', '', { shouldValidate: true });
            setValue('contract', '', { shouldValidate: true });
        }
    };

    const handleChangeType = (e: string | null) => {
        setValue('type', e, { shouldValidate: true });
        clearFieldsThirdPartyPayer(e);
    };

    return (
        <div>
            {changeTitulaire ? (
                <div className="space-y-5">
                    <Button type="button" variant="outline" size="sm" onClick={handleChangeTitulaire} className="!border-destructive !text-destructive hover:!border-destructive/70 hover:!text-destructive/70 cursor-pointer">
                        <X className="w-3.5 h-3.5" />
                        Annuler
                    </Button>

                    <Text
                        type={'text'}
                        name="titulaire"
                        label={'Nom du titulaire du compte'}
                        onBlur={handleBlur}
                        value={values.titulaire}
                        error={errors.titulaire?.message}
                        onChange={(e) => {
                            handleChange(e);
                            handleChangeTitulaireField(e.target.value);
                        }}
                        required
                    />

                    {contrat.id && values.titulaire && (souscripteur ?? '').trim().toUpperCase() !== values.titulaire.trim().toUpperCase() && (
                        <div className="space-y-5">
                            <RadioGroup
                                label={"Type de tiers payeur"}
                                name={'type'}
                                setValue={setValue}
                                options={[
                                    { value: "P", label: "Personne physique" },
                                    { value: "M", label: "Personne morale" }
                                ]}
                                value={values.type}
                                onChange={(e) => handleChangeType(e.target.value)}
                                required
                            />

                            {values.type === 'P' && (
                                <div className="space-y-5">
                                    <Select
                                        name="relationship"
                                        label={'Votre lien avec le tiers payeur'}
                                        value={values.relationship}
                                        error={errors.relationship?.message}
                                        onChange={e => {
                                            setValue('relationship', e.target.value, { shouldValidate: true });
                                            if (e.target.value !== 'A') {
                                                setValue('third_payeur_reason', '', { shouldValidate: true });
                                            }
                                        }}
                                        required
                                    >
                                        <option value="">Sélectionnez une relation</option>
                                        {tiersPayeurRelation.map((relation, index) => (
                                            <option key={index} value={relation.code}>{relation.libelle}</option>
                                        ))}
                                    </Select>

                                    {values.relationship === 'A' && (
                                        <Text
                                            type={'text'}
                                            name="third_payeur_reason"
                                            label={"Motif d'intervention du payeur"}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.third_payeur_reason}
                                            error={errors.third_payeur_reason?.message}
                                            maxLength={250}
                                            required
                                        />
                                    )}
                                </div>
                            )}

                            <Text
                                type={'text'}
                                name="lastname"
                                autoComplete={values.type === 'P' ? 'family-name' : 'organization'}
                                label={values.type === 'P' ? 'Nom' : 'Nom de la société'}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.lastname}
                                error={errors.lastname?.message}
                                normalize={(v: string) => v.toUpperCase()}
                                maxLength={50}
                                required
                            />

                            {values.type === 'P' && (
                                <div className="space-y-5">
                                    <Text
                                        type={'text'}
                                        name="birthname"
                                        label={'Nom de naissance'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.birthname}
                                        error={errors.birthname?.message}
                                        normalize={(v: string) => v.toUpperCase()}
                                        maxLength={20}
                                        required
                                    />

                                    <Text
                                        type={'text'}
                                        name="firstname"
                                        autoComplete="given-name"
                                        label={'Prénom'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.firstname}
                                        error={errors.firstname?.message}
                                        normalize={(v: string) => v.toUpperCase()}
                                        maxLength={20}
                                        required
                                    />

                                    <SimpleDatePicker
                                        label={'Date de naissance'}
                                        name={'birthdate'}
                                        setValue={(name, value) => setValue(name as 'birthdate', value)}
                                        value={values.birthdate}
                                        error={errors.birthdate?.message}
                                        maxDate={new Date(formatDate(subYears(new Date(), 14), DATE_ISO))}
                                        required
                                    />

                                    <div>
                                        {countries && (
                                            <Autocomplete
                                                name="birthcountry"
                                                label="Pays de naissance"
                                                data={countries}
                                                defaultValue={countries.find(c => c.value === values.birthcountry)?.label}
                                                setValue={setValue}
                                                error={errors.birthcountry?.message}
                                                required
                                            />
                                        )}
                                    </div>

                                    <div>
                                        {values.birthcountry === 'FRA' ? (
                                            <AutoCompleteBirth
                                                name="birthcity"
                                                label="Ville de naissance"
                                                country={values.birthcountry}
                                                defaultValue={values.birthcity}
                                                setValue={setValue}
                                                error={errors.birthcity?.message}
                                                required
                                            />
                                        ) : (
                                            <Text
                                                type="text"
                                                name="birthcity"
                                                label="Ville de naissance"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.birthcity}
                                                error={errors.birthcity?.message}
                                                required
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <Text
                        type={'text'}
                        name="adresse"
                        autoComplete="street-address"
                        label={'Adresse du titulaire du compte'}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.adresse}
                        error={errors.adresse?.message}
                        required
                    />

                    <PostalCodeCity
                        label={'Code postal et ville'}
                        cp="cp"
                        ville="ville"
                        error={errors.ville?.message}
                        required
                    />
                </div>
            ) : (
                <div className="flex items-center justify-between p-6 bg-muted/50 rounded-lg">
                    <div>
                        <p className="text-sm text-muted-foreground mb-0.5">Titulaire du compte</p>
                        <p className="font-medium">{titulaire}</p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => setChangeTitulaire(true)} className="cursor-pointer">
                        <Pencil className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Changer le titulaire</span>
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ChangeTitulaire;
